/* eslint-disable max-lines */
import { Dispatch, Action } from "redux";
import { ThunkAction } from "redux-thunk";
import { loadAndTrack } from "redux-request-loading";
import * as moment from "moment";

import { beginAjaxCall, Payload } from "@neworbit/redux-helpers";

import { toast } from "@teamprevent/common/toasts";

import { PhysiotherapyReferralCaseCreateModel } from "../physiotherapy/models";

import * as actions from "./actionTypes";
import QuestionnaireApi from "./QuestionnaireApi"; // eslint-disable-line import/no-named-as-default
import { AnsweredQuestion, AnsweredQuestionWithId } from "./model";
import { AUTO_SAVE_REFERRAL_FORM_SUCCESS } from "./components/referralForm/actionTypes";
import { AUTO_SAVE_HEALTH_SURVEILLANCE_REVIEW_SUCCESS } from "./components/healthSurveillance/review/actionTypes";
import { AUTO_SAVE_IMMUNISATION_REVIEW_SUCCESS } from "./components/immunisationReview/actionTypes";

interface QuestionnairePayload {
    questionnaireKey: string;
}

export interface LoadedQuestionnairePayload extends QuestionnairePayload {
    questions: AnsweredQuestionWithId[];
    eTag?: string | null;
    referralSubmittedDate?: moment.Moment;
    isDefaultCustomerType?: boolean;
    customerType?: string;
    reportChangesBlocked?: boolean;
    releaseReportComments?: string;
    approvalState?: string;
    isConsentWithdrawn?: boolean;
}

export interface UpdateQuestionPayload extends QuestionnairePayload {
    sectionId: string;
    id: string;
    question: AnsweredQuestion;
    title?: string;
}

export interface ReviewHealthSurveillanceResultsAutoSavePayload extends QuestionnairePayload {
    eTag?: string;
}

export interface ImmunisationReviewAutoSavePayload extends QuestionnairePayload {
    eTag?: string;
}

interface RemoveQuestionPayload extends QuestionnairePayload {
    sectionId: string;
    id: string;
}

interface RegisterSectionPayload extends QuestionnairePayload {
    sectionId: string;
    active: boolean | undefined;
    index: number | undefined;
    title: string;
    neverHide: boolean;
}

interface RegisterSectionIndexIfNeededPayload extends QuestionnairePayload {
    sectionId: string;
    index: number;
}

interface ToggleSectionPayload extends QuestionnairePayload {
    sectionId: string;
    closeOthers?: boolean;
}

interface NextSectionPayload extends QuestionnairePayload {
    sectionId: string;
    nextSectionId?: string;
}

interface ValidateQuestionPayload extends QuestionnairePayload {
    sectionId: string;
    id: string;
    validatedQuestion: Promise<AnsweredQuestion>;
}

interface SubmitInvalidQuestionnairePayload extends QuestionnairePayload {
    openFirstInvalid?: boolean;
}

interface SaveQuestionnaireSuccessPayload extends QuestionnairePayload {
    eTag: string;
}

interface SectionIdPayload extends QuestionnairePayload {
    sectionId: string;
}

interface UserRemoveSectionPayload extends QuestionnairePayload {
    sectionId: string;
    id: string;
}

export type QuestionnaireLoaded = ({ type: typeof actions.QUESTIONNAIRE_LOADED }) & Payload<LoadedQuestionnairePayload>;
export type SubmitQuestionnaireSuccess = ({ type: typeof actions.SUBMIT_QUESTIONNAIRE_SUCCESS }) & Payload<QuestionnairePayload>;
export type SubmitInvalidQuestionnaire = ({ type: typeof actions.SUBMIT_INVALID_QUESTIONNAIRE }) & Payload<SubmitInvalidQuestionnairePayload>;
export type RegisterSection = ({ type: typeof actions.REGISTER_SECTION }) & Payload<RegisterSectionPayload>;
export type RegisterSectionId = ({ type: typeof actions.REGISTER_SECTION_ID }) & Payload<SectionIdPayload>;
export type RegisterSectionIndexIfNeeded = ({ type: typeof actions.REGISTER_SECTION_INDEX_IF_NEEDED}) & Payload<RegisterSectionIndexIfNeededPayload>;
export type UpdateQuestion = ({ type: typeof actions.UPDATE_QUESTION }) & Payload<UpdateQuestionPayload>;
export type RemoveQuestion = ({ type: typeof actions.REMOVE_QUESTION }) & Payload<RemoveQuestionPayload>;
export type UserRemoveQuestion = ({ type: typeof actions.USER_REMOVE_QUESTION }) & Payload<UserRemoveSectionPayload>;
export type ToggleSection = ({ type: typeof actions.TOGGLE_SECTION }) & Payload<ToggleSectionPayload>;
export type OpenSection = ({ type: typeof actions.OPEN_SECTION }) & Payload<ToggleSectionPayload>;
export type NextSection = ({ type: typeof actions.NEXT_SECTION }) & Payload<NextSectionPayload>;
export type ClearQuestionnaire = ({ type: typeof actions.CLEAR_QUESTIONNAIRE }) & Payload<QuestionnairePayload>;
export type ResetActiveSections = ({ type: typeof actions.RESET_ACTIVE_SECTIONS }) & Payload<QuestionnairePayload>;
export type ValidateQuestionComplete = ({ type: typeof actions.VALIDATE_QUESTION_COMPLETE }) & Payload<UpdateQuestionPayload>;
export type SaveQuestionnaireSuccess = ({ type: typeof actions.SAVE_QUESTIONNAIRE_SUCCESS }) & Payload<SaveQuestionnaireSuccessPayload>;
export type RemoveSection = ({ type: typeof actions.REMOVE_SECTION }) & Payload<SectionIdPayload>;
export type AutoSaveReferralForm = ({ type: typeof AUTO_SAVE_REFERRAL_FORM_SUCCESS }) & Payload<LoadedQuestionnairePayload & { isInitial: boolean }>;
export type AutoSaveHealthSurveillanceReview = ({ type: typeof AUTO_SAVE_HEALTH_SURVEILLANCE_REVIEW_SUCCESS })
    & Payload<ReviewHealthSurveillanceResultsAutoSavePayload>;
export type AutoSaveImmunisationReview = ({ type: typeof AUTO_SAVE_IMMUNISATION_REVIEW_SUCCESS })
    & Payload<ImmunisationReviewAutoSavePayload>;

export type QuestionnaireAction =
    QuestionnaireLoaded |
    SubmitQuestionnaireSuccess |
    SubmitInvalidQuestionnaire |
    RegisterSection |
    RegisterSectionId |
    RegisterSectionIndexIfNeeded |
    UpdateQuestion |
    RemoveQuestion |
    UserRemoveQuestion |
    ToggleSection |
    OpenSection |
    ClearQuestionnaire |
    ResetActiveSections |
    NextSection |
    ValidateQuestionComplete |
    SaveQuestionnaireSuccess |
    RemoveSection |
    AutoSaveReferralForm |
    AutoSaveHealthSurveillanceReview |
    AutoSaveImmunisationReview;

export const SUBMIT_QUESTIONNAIRE = "SUBMIT_QUESTIONNAIRE";

export function questionnaireLoaded(payload: LoadedQuestionnairePayload): QuestionnaireAction {
    return {
        type: actions.QUESTIONNAIRE_LOADED,
        payload
    };
}

export function submitQuestionnaireSuccess(questionnaireKey: string): QuestionnaireAction {
    return {
        type: actions.SUBMIT_QUESTIONNAIRE_SUCCESS,
        payload: { questionnaireKey }
    };
}

export function submitInvalidQuestionnaire(questionnaireKey: string, openFirstInvalid?: boolean): QuestionnaireAction {
    return {
        type: actions.SUBMIT_INVALID_QUESTIONNAIRE,
        payload: { questionnaireKey, openFirstInvalid }
    };
}

export function registerSection(payload: RegisterSectionPayload): QuestionnaireAction {
    return {
        type: actions.REGISTER_SECTION,
        payload
    };
}

export function registerSectionId(payload: SectionIdPayload): QuestionnaireAction {
    return {
        type: actions.REGISTER_SECTION_ID,
        payload
    };
}

export function registerSectionIndexIfNeeded(payload: RegisterSectionIndexIfNeededPayload): QuestionnaireAction {
    return {
        type: actions.REGISTER_SECTION_INDEX_IF_NEEDED,
        payload
    };
}

export function updateQuestion(payload: UpdateQuestionPayload): QuestionnaireAction {
    return {
        type: actions.UPDATE_QUESTION,
        payload
    };
}

export function validateQuestionComplete(payload: UpdateQuestionPayload): QuestionnaireAction {
    return {
        type: actions.VALIDATE_QUESTION_COMPLETE,
        payload
    };
}

export function removeQuestion(payload: RemoveQuestionPayload): QuestionnaireAction {
    return {
        type: actions.REMOVE_QUESTION,
        payload
    };
}

export function userRemoveQuestion(payload: UserRemoveSectionPayload): QuestionnaireAction {
    return {
        type: actions.USER_REMOVE_QUESTION,
        payload
    };
}

export function toggleSection(payload: ToggleSectionPayload) {
    return {
        type: actions.TOGGLE_SECTION,
        payload
    };
}

export function openSection(payload: ToggleSectionPayload) {
    return {
        type: actions.OPEN_SECTION,
        payload
    };
}

export function nextSection(payload: NextSectionPayload) {
    return {
        type: actions.NEXT_SECTION,
        payload
    };
}

export const saveQuestionnaireSuccess = (payload: SaveQuestionnaireSuccessPayload) => {
    return {
        type: actions.SAVE_QUESTIONNAIRE_SUCCESS,
        payload
    };
};

export function validateQuestion(payload: ValidateQuestionPayload) {
    return async (dispatch: Dispatch) => {
        const validatedQuestion = await payload.validatedQuestion;
        dispatch(validateQuestionComplete({
            question: validatedQuestion,
            id: payload.id,
            sectionId: payload.sectionId,
            questionnaireKey: payload.questionnaireKey
        }));
    };
}

export function clearQuestionnaire(questionnaireKey: string): ClearQuestionnaire {
    return {
        type: actions.CLEAR_QUESTIONNAIRE,
        payload: { questionnaireKey }
    };
}

export const resetActiveSections = (questionnaireKey: string): ResetActiveSections => {
    return {
        type: actions.RESET_ACTIVE_SECTIONS,
        payload: { questionnaireKey }
    };
};

export const removeSection = (questionnaireKey: string, sectionId: string) => {
    return {
        type: actions.REMOVE_SECTION,
        payload: { questionnaireKey, sectionId }
    };
};

export function submitQuestionnaire(questions: AnsweredQuestionWithId[], apiUrl: string, questionnaireName: string, questionnaireKey: string):
    ThunkAction<any, any, undefined, Action> {
    if (!questions.every(question => question.isValid)) {
        return (dispatch: Dispatch) => dispatch(submitInvalidQuestionnaire(questionnaireKey, true));
    }
    return async (dispatch: Dispatch) => {
        const response = await loadAndTrack(dispatch, SUBMIT_QUESTIONNAIRE, QuestionnaireApi.submit(questions, apiUrl));
        toast.success(`${questionnaireName} submitted successfully`);
        dispatch(submitQuestionnaireSuccess(questionnaireKey));
        return response.data;
    };
}

export function loadQuestionnaire(apiUrl: string, customerId: string, id: string, caseId: string, questionnaireKey: string) {
    return async (dispatch: Dispatch) => {
        dispatch(beginAjaxCall("loadQuestionnaire"));
        const response = (await QuestionnaireApi.load(customerId, id, caseId, apiUrl)).data;
        const questions = response.data.questions as AnsweredQuestionWithId[];
        const referralSubmittedDate = response.referralSubmittedDate;
        dispatch(questionnaireLoaded({ questions, questionnaireKey, referralSubmittedDate }));
    };
}

export function submitPhysiotherapyReferralQuestionnaire(
    physiotherapyReferralCase: PhysiotherapyReferralCaseCreateModel, apiUrl: string, questionnaireName: string, questionnaireKey: string):
    ThunkAction<any, any, undefined, Action> {
    const questions = physiotherapyReferralCase.formData.questions;
    if (!questions.every(question => question.isValid)) {
        return (dispatch: Dispatch) => dispatch(submitInvalidQuestionnaire(questionnaireKey, true));
    }
    return async (dispatch: Dispatch) => {
        const response = await loadAndTrack(
            dispatch,
            SUBMIT_QUESTIONNAIRE,
            QuestionnaireApi.submitPhysiotherapyReferralQuestionnaire(physiotherapyReferralCase, apiUrl));
        toast.success(`${questionnaireName} submitted successfully`);
        dispatch(submitQuestionnaireSuccess(questionnaireKey));
        return response.data;
    };
}
